// One drawer, one tab strip, one open panel. The side panels used to
// be six independent booleans rendering six independent panes at the
// same edge with nothing arbitrating between them; now they are a single
// `PanelId | null`, and switching is a tab click rather than closing one
// thing to open another.
//
// The panels keep their own bodies and headers — this owns the column
// and the tabs above them, so the same drawer can be a right-hand pane
// on a desktop and a full-height sheet over the document on a phone.
import type { ReactNode } from "react";

export type PanelId =
  | "pageSetup"
  | "decor"
  | "bookmarks"
  | "comments"
  | "fonts"
  | "objects"
  | "properties"
  | "accessibility";

/** Tab order, left to right: the document's own inventories first, then
 *  the page-shaping panels — roughly "find something" before "change
 *  the paper". */
export const PANEL_TABS: readonly { id: PanelId; icon: string; label: string }[] = [
  { id: "objects", icon: "ri-stack-line", label: "Objects" },
  { id: "comments", icon: "ri-chat-3-line", label: "Comments" },
  { id: "bookmarks", icon: "ri-bookmark-line", label: "Bookmarks" },
  { id: "fonts", icon: "ri-font-family", label: "Fonts" },
  { id: "pageSetup", icon: "ri-file-paper-2-line", label: "Page setup" },
  { id: "decor", icon: "ri-layout-top-line", label: "Headers" },
  { id: "properties", icon: "ri-file-info-line", label: "Properties" },
  { id: "accessibility", icon: "ri-wheelchair-line", label: "Accessibility" },
];

export function PanelDrawer({
  panel,
  onSelect,
  fullScreen = false,
  children,
}: {
  panel: PanelId;
  onSelect: (id: PanelId) => void;
  /** Phone layout: cover the document instead of sitting beside it. */
  fullScreen?: boolean;
  children: ReactNode;
}) {
  return (
    <aside
      aria-label="Document panels"
      className={
        fullScreen
          ? "absolute inset-0 z-40 flex flex-col bg-[color:var(--color-bg)]"
          : // The right column owns the width, the border and the resizer
            // — the drawer just fills whatever is left under the options
            // panel that may sit above it.
            "flex min-h-0 flex-1 flex-col"
      }
    >
      <div
        role="tablist"
        aria-label="Panels"
        className="flex shrink-0 items-center gap-0.5 overflow-x-auto border-b border-[color:var(--color-border)] px-1 py-1"
      >
        {PANEL_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={panel === t.id}
            title={t.label}
            onClick={() => onSelect(t.id)}
            className={`inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md px-2 text-xs ${
              panel === t.id
                ? "bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]"
                : "text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-subtle)]"
            }`}
          >
            <i className={t.icon} aria-hidden />
            {/* The open tab always names itself; the rest are icons
                until there's room, so the strip never scrolls on a
                pane the user has deliberately made narrow. */}
            <span className={panel === t.id ? "" : "hidden 2xl:inline"}>{t.label}</span>
          </button>
        ))}
      </div>
      <div className="flex min-h-0 flex-1">{children}</div>
    </aside>
  );
}
